import type { Sensor } from '../../domain/entities/Sensor';
import type { Umbral } from '../../domain/entities/Umbral';
import type { Lectura } from '../../domain/value-objects/Lectura';
import { ErrorApp, registrarError, traducirErrorSupabase } from '../../domain/errores';
import { aBooleano, aNumero, aTexto } from '../mappers/conversiones';
import {
  aLectura,
  aSensor,
  aUmbralAnalogico,
  aUmbralDigital,
  type FilaSensor,
  type FilaUltimoValor,
  type FilaUmbralAnalogico,
  type FilaUmbralDigital,
} from '../mappers/sensorMapper';
import { supabase } from '../supabase/cliente';
import { ambitoSesion } from './AmbitoSesion';
import { filtroDemo } from './FiltroDemo';

export interface FiltrosSensores {
  clienteId?: number | null;
  areaId?: number | null;
  nodoId?: number | null;
  /** Incluye también los sensores dados de baja. */
  incluirInactivos?: boolean;
}

/**
 * Conexión de un nodo tal como la calcula el servidor.
 * `enLinea` es `null` cuando el nodo no reporta latido y por tanto su estado
 * no puede saberse; mostrarlo como «desconectado» sería engañoso.
 */
export interface PresenciaNodo {
  nodoId: number;
  enLinea: boolean | null;
  vigilaConexion: boolean;
  /** Cada cuántos segundos se espera señal de vida. */
  cadaSegundos: number | null;
}

/**
 * Acceso a los sensores, sus umbrales y su última lectura.
 *
 * Esta información vive repartida en cinco sitios distintos de la base de
 * datos. El repositorio los reúne en una sola operación por lotes, de modo que
 * el resto de la aplicación recibe sensores ya completos y no tiene que saber
 * de dónde salió cada pieza.
 *
 * La versión anterior pedía el umbral de cada sensor por separado, lo que en
 * una instalación con cien sensores significaba cien consultas.
 */
export class RepositorioSensores {
  async listarConValores(filtros: FiltrosSensores = {}): Promise<Sensor[]> {
    const filas = await this.leerMetadatos(filtros);
    if (filas.length === 0) return [];

    const ids = filas
      .map((f) => aNumero(f.sensor_id) ?? aNumero(f.id))
      .filter((id): id is number => id !== null);

    // Las cuatro consultas son independientes entre sí: se lanzan a la vez.
    const [categorias, umbrales, lecturas] = await Promise.all([
      this.leerCategorias(ids),
      this.leerUmbrales(ids),
      this.leerUltimasLecturas(ids),
    ]);

    return filas
      .map((fila) => {
        const id = aNumero(fila.sensor_id) ?? aNumero(fila.id) ?? 0;
        return aSensor(fila, {
          categoria: categorias.get(id) ?? null,
          umbral: umbrales.get(id) ?? null,
          lectura: lecturas.get(id) ?? null,
        });
      })
      .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  }

  private async leerMetadatos(filtros: FiltrosSensores): Promise<FilaSensor[]> {
    let consulta = filtroDemo.aplicar(
      'vw_sensores_detallado',
      supabase.from('vw_sensores_detallado').select('*'),
    );

    if (!filtros.incluirInactivos) consulta = consulta.eq('estado', 'activo');
    consulta = ambitoSesion.aplicar(consulta);
    if (filtros.clienteId) consulta = consulta.eq('cliente_id', filtros.clienteId);
    if (filtros.areaId) consulta = consulta.eq('area_id', filtros.areaId);
    if (filtros.nodoId) consulta = consulta.eq('nodo_id', filtros.nodoId);

    const { data, error } = await consulta;
    if (error) throw traducirErrorSupabase(error);

    return (data ?? []) as FilaSensor[];
  }

  /**
   * La categoría vive en la tabla `sensores` y la vista no la trae.
   * Si falla, los sensores se muestran sin categoría en vez de no mostrarse:
   * es un dato de clasificación, no de operación.
   */
  private async leerCategorias(ids: readonly number[]): Promise<Map<number, string | null>> {
    const mapa = new Map<number, string | null>();
    if (ids.length === 0) return mapa;

    const { data, error } = await supabase
      .from('sensores')
      .select('id, categoria')
      .in('id', [...ids]);

    if (error) {
      registrarError('sensores.categorias', error);
      return mapa;
    }

    for (const fila of (data ?? []) as Record<string, unknown>[]) {
      const id = aNumero(fila.id);
      if (id !== null) mapa.set(id, aTexto(fila.categoria));
    }

    return mapa;
  }

  /** Umbrales analógicos y digitales de una vez, no de uno en uno. */
  private async leerUmbrales(ids: readonly number[]): Promise<Map<number, Umbral>> {
    const mapa = new Map<number, Umbral>();
    if (ids.length === 0) return mapa;

    const [analogicos, digitales] = await Promise.all([
      supabase
        .from('umbrales_analogicos')
        .select('*')
        .in('sensor_id', [...ids]),
      supabase
        .from('umbrales_digitales')
        .select('*')
        .in('sensor_id', [...ids]),
    ]);

    if (analogicos.error) registrarError('sensores.umbralesAnalogicos', analogicos.error);
    else {
      for (const fila of (analogicos.data ?? []) as FilaUmbralAnalogico[]) {
        const sensorId = aNumero(fila.sensor_id);
        if (sensorId !== null) mapa.set(sensorId, aUmbralAnalogico(fila, sensorId));
      }
    }

    if (digitales.error) registrarError('sensores.umbralesDigitales', digitales.error);
    else {
      for (const fila of (digitales.data ?? []) as FilaUmbralDigital[]) {
        const sensorId = aNumero(fila.sensor_id);
        if (sensorId !== null) mapa.set(sensorId, aUmbralDigital(fila, sensorId));
      }
    }

    return mapa;
  }

  private async leerUltimasLecturas(ids: readonly number[]): Promise<Map<number, Lectura>> {
    const mapa = new Map<number, Lectura>();
    if (ids.length === 0) return mapa;

    const { data, error } = await supabase
      .from('last_sensor_values')
      .select('sensor_id, valor, measured_at')
      .in('sensor_id', [...ids]);

    if (error) {
      registrarError('sensores.ultimasLecturas', error);
      return mapa;
    }

    for (const fila of (data ?? []) as FilaUltimoValor[]) {
      const sensorId = aNumero(fila.sensor_id);
      const lectura = aLectura(fila);
      if (sensorId !== null && lectura) mapa.set(sensorId, lectura);
    }

    return mapa;
  }

  /**
   * Cambia la vigilancia de conexión de un nodo.
   *
   * Vive en `nodos_runtime` y no en `nodos`: la tabla del nodo describe qué
   * es y dónde está, y esta describe cómo se comporta en marcha. Con
   * `vigilar` en falso el nodo queda en modo `fixed` y deja de considerarse
   * caído cuando no reporta, que es lo que se quiere en un equipo que sólo
   * habla cuando tiene algo que decir.
   */
  async configurarLatido(nodoId: number, vigilar: boolean, cadaSegundos: number): Promise<void> {
    /*
     * El plazo se guarda siempre, se vigile o no.
     *
     * Al dejar de vigilar es tentador vaciarlo, pero la columna no admite
     * nulos y además guardarlo tiene sentido: si mañana se vuelve a activar,
     * el valor que había sigue ahí en vez de volver al de fábrica.
     */
    /*
     * `update` y no `upsert`. La fila de cada nodo ya existe, y el `insert` que
     * lleva dentro un upsert obliga a rellenar columnas que aquí no se tocan:
     * la petición se rechazaba entera con un 400. Es además lo que hacía la
     * aplicación anterior, que funcionaba.
     */
    const { data, error } = await supabase
      .from('nodos_runtime')
      .update({
        presence_mode: vigilar ? 'auto' : 'fixed',
        target_presence_s: Math.max(10, Math.round(cadaSegundos)),
      })
      .eq('nodo_id', nodoId)
      .select('nodo_id');

    if (error) throw traducirErrorSupabase(error);

    /*
     * Un update que no encuentra su fila no da error: no cambia nada y
     * devuelve vacío. Sin comprobarlo, la pantalla diría «guardado» sobre algo
     * que no se guardó, que es peor que fallar.
     */
    if (!data || data.length === 0) {
      throw new ErrorApp(
        'NO_ENCONTRADO',
        'Este nodo aún no tiene ficha de conexión. Se crea con su primera señal de vida: inténtalo cuando el equipo haya reportado al menos una vez.',
      );
    }
  }

  /** Conexión de los nodos indicados, según la calcula el servidor. */
  async presenciaDeNodos(nodoIds: readonly number[]): Promise<Map<number, PresenciaNodo>> {
    const mapa = new Map<number, PresenciaNodo>();
    if (nodoIds.length === 0) return mapa;

    const { data, error } = await supabase
      .from('vw_nodos_presence')
      .select('nodo_id, presence_mode, is_online, target_presence_s')
      .in('nodo_id', [...nodoIds]);

    if (error) {
      registrarError('sensores.presencia', error);
      return mapa;
    }

    for (const fila of (data ?? []) as Record<string, unknown>[]) {
      const nodoId = aNumero(fila.nodo_id);
      if (nodoId === null) continue;

      const vigila = aTexto(fila.presence_mode) === 'auto';

      mapa.set(nodoId, {
        nodoId,
        vigilaConexion: vigila,
        enLinea: vigila ? aBooleano(fila.is_online) : null,
        cadaSegundos: aNumero(fila.target_presence_s),
      });
    }

    return mapa;
  }
}

export const repositorioSensores = new RepositorioSensores();
